import React, { useEffect, useRef, useState, useCallback } from 'react';
import classNames from 'classnames';

const SKEEPERS_PRODUCT_SCRIPT_SRC =
  'https://widgets.rr.skeepers.io/product/03c78e48-c424-42d7-8037-773a37af650b/51793326-40f9-4c71-9cde-604c3edae6ba.js';

const WIDGET_CHECK_MAX_MS = 12000;
const WIDGET_POLL_MS = 250;
const SCRIPT_RETRY_MAX = 2;
const SCRIPT_RETRY_DELAY_MS = 2500;

const triggerSkeepersProductGenerate = detail => {
  if (typeof window === 'undefined') {
    return false;
  }
  const generateFn = window.SkeepersProductWidget?.generate;
  if (typeof generateFn !== 'function') {
    logSkeepers('generate:unavailable', detail);
    return false;
  }
  try {
    generateFn();
    logSkeepers('generate:called', detail);
    return true;
  } catch (error) {
    logSkeepers('generate:error', { ...detail, message: error?.message || 'unknown_error' });
    return false;
  }
};

const shouldLogSkeepersProduct = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  if (window['__NEOKASA_SKEEPERS_DEBUG__']) {
    return true;
  }
  try {
    return process.env.NODE_ENV === 'development';
  } catch (e) {
    return false;
  }
};

const logSkeepers = (step, detail = {}) => {
  if (!shouldLogSkeepersProduct()) {
    return;
  }
  // eslint-disable-next-line no-console
  console.log('[SkeepersProduct]', step, detail);
};

const describeWidgetContent = el => {
  if (!el) {
    return { has: false, reason: 'no_element' };
  }
  if (el.querySelector?.('iframe')) {
    return { has: true, reason: 'iframe' };
  }
  if (el.childNodes && el.childNodes.length > 0) {
    return { has: true, reason: 'child_nodes', count: el.childNodes.length };
  }
  const html = typeof el.innerHTML === 'string' ? el.innerHTML.trim() : '';
  if (html.length > 24) {
    return { has: true, reason: 'inner_html', htmlLength: html.length };
  }
  try {
    const h = el.getBoundingClientRect?.().height;
    if (typeof h === 'number' && h > 28) {
      return { has: true, reason: 'height', height: Math.round(h) };
    }
  } catch (e) {
    return { has: false, reason: 'rect_error' };
  }
  return {
    has: false,
    reason: 'empty',
    htmlLength: html.length,
    height: (() => {
      try {
        return Math.round(el.getBoundingClientRect?.().height || 0);
      } catch (e2) {
        return null;
      }
    })(),
  };
};

const widgetHasRenderedContent = el => describeWidgetContent(el).has;

/**
 * Liste d'avis produit Avis Vérifiés / Skeepers (`data-product-id` = SKU Neokasa, `publicData.sku`).
 * N’affiche le bloc (titre + widget) que si le script injecte du contenu ; sinon `null` après délai max.
 *
 * Logs console `[SkeepersProduct]` en développement, ou en prod après :
 * `window.__NEOKASA_SKEEPERS_DEBUG__ = true` (recharger la page si besoin).
 */
const SkeepersProductReviews = props => {
  const {
    productIds,
    productTitle = '',
    productPrice = '',
    currency = '',
    productUrl = '',
    imageUrl = '',
    locale = 'fr_FR',
    rootClassName,
    className,
    sectionClassName,
    headingClassName,
    onResolve,
  } = props;

  const ids = typeof productIds === 'string' ? productIds.trim() : '';
  const widgetRootRef = useRef(null);
  const resolvedRef = useRef(false);
  const onResolveRef = useRef(onResolve);
  onResolveRef.current = onResolve;

  const [show, setShow] = useState(null);
  const watchStartedAtRef = useRef(null);

  const notify = useCallback(hasContent => {
    if (resolvedRef.current) {
      return;
    }
    resolvedRef.current = true;
    // n'appelle setShow que si nécessaire pour éviter un rendu inutile
    if (!hasContent) {
      setShow(false);
    } else {
      setShow(true);
    }
    const elapsedMs =
      watchStartedAtRef.current != null && typeof performance !== 'undefined'
        ? Math.round(performance.now() - watchStartedAtRef.current)
        : null;
    logSkeepers(hasContent ? 'resolved:show_widget' : 'resolved:hide_no_content', {
      dataProductId: ids,
      elapsedMs,
    });
    onResolveRef.current?.(hasContent);
  }, [ids]);

  useEffect(() => {
    resolvedRef.current = false;
    setShow(null);
    logSkeepers('reset', { dataProductId: ids || '(empty)' });
  }, [ids]);

  useEffect(() => {
    if (!ids || typeof document === 'undefined') {
      return undefined;
    }

    let disposed = false;
    let retryTimeoutId = null;
    let retries = 0;

    const hasWidgetContent = () => widgetHasRenderedContent(widgetRootRef.current);
    const clearRetryTimeout = () => {
      if (retryTimeoutId) {
        clearTimeout(retryTimeoutId);
        retryTimeoutId = null;
      }
    };

    const scheduleRetry = () => {
      if (disposed || retries >= SCRIPT_RETRY_MAX || hasWidgetContent()) {
        return;
      }
      clearRetryTimeout();
      retries += 1;
      retryTimeoutId = setTimeout(() => {
        if (!disposed) {
          loadScript(true);
        }
      }, SCRIPT_RETRY_DELAY_MS);
    };

    const verifyPostGenerate = () => {
      // Le script peut s'exécuter sans injecter immédiatement le widget.
      // On vérifie peu après puis on retente un reload si nécessaire.
      setTimeout(() => {
        if (!disposed && !hasWidgetContent()) {
          scheduleRetry();
        }
      }, 600);
    };

    const loadScript = forceReload => {
      let script = document.querySelector(`script[src="${SKEEPERS_PRODUCT_SCRIPT_SRC}"]`);
      if (forceReload && script) {
        script.remove();
        script = null;
      }

      if (!script) {
        script = document.createElement('script');
        script.src = SKEEPERS_PRODUCT_SCRIPT_SRC;
        script.setAttribute('data-prevent-default-loading', 'true');
        script.charset = 'utf-8';
        document.body.appendChild(script);
        logSkeepers('script:appended', {
          src: SKEEPERS_PRODUCT_SCRIPT_SRC,
          dataProductId: ids,
          preventDefaultLoading: true,
          forceReload,
        });
      }

      script.onload = () => {
        triggerSkeepersProductGenerate({
          source: forceReload ? 'script_onload_retry' : 'script_onload',
          dataProductId: ids,
          retries,
        });
        verifyPostGenerate();
      };
      script.onerror = () => {
        logSkeepers('script:error', { dataProductId: ids, retries });
        scheduleRetry();
      };

      if (!forceReload) {
        triggerSkeepersProductGenerate({ source: 'existing_script', dataProductId: ids, retries });
        verifyPostGenerate();
      }
    };

    // Cas SPA recommandé par Skeepers:
    // - empêcher le chargement auto via l'attribut data-prevent-default-loading
    // - appeler SkeepersProductWidget.generate() lors de l'affichage dynamique.
    loadScript(false);

    return () => {
      disposed = true;
      clearRetryTimeout();
      const script = document.querySelector(`script[src="${SKEEPERS_PRODUCT_SCRIPT_SRC}"]`);
      if (script) {
        script.onload = null;
        script.onerror = null;
      }
    };
  }, [ids]);

  useEffect(() => {
    if (!ids) {
      return undefined;
    }
    const el = widgetRootRef.current;
    if (!el) {
      logSkeepers('watch:skip_no_dom_node', { dataProductId: ids });
      return undefined;
    }

    watchStartedAtRef.current = typeof performance !== 'undefined' ? performance.now() : null;
    logSkeepers('watch:start', {
      dataProductId: ids,
      pollMs: WIDGET_POLL_MS,
      maxWaitMs: WIDGET_CHECK_MAX_MS,
      dataLocale: locale,
      dataCurrency: currency,
      dataPrice: productPrice,
      dataName: productTitle,
      dataUrl: productUrl,
      hasImageUrl: Boolean(imageUrl),
    });

    const check = (source = 'check') => {
      if (resolvedRef.current) {
        return;
      }
      const diag = describeWidgetContent(el);
      if (diag.has) {
        logSkeepers('watch:content_detected', { source, ...diag });
        notify(true);
      }
    };

    const mo = new MutationObserver(() => check('mutation'));
    mo.observe(el, { childList: true, subtree: true, attributes: true, characterData: true });

    const poll = setInterval(() => check('poll'), WIDGET_POLL_MS);
    const timeout = setTimeout(() => {
      if (!resolvedRef.current) {
        const diag = describeWidgetContent(el);
        logSkeepers('watch:timeout_no_content', { maxWaitMs: WIDGET_CHECK_MAX_MS, ...diag });
        notify(false);
      }
    }, WIDGET_CHECK_MAX_MS);

    check('initial');

    return () => {
      logSkeepers('watch:cleanup', { dataProductId: ids });
      mo.disconnect();
      clearInterval(poll);
      clearTimeout(timeout);
    };
  }, [ids, notify, locale, currency, productPrice, productTitle, productUrl, imageUrl]);

  if (!ids) {
    return null;
  }

  if (show === false) {
    return null;
  }

  const isPending = show === null;

  return (
    <div
      className={classNames(sectionClassName, rootClassName, className)}
      aria-busy={isPending ? 'true' : undefined}
    >
      {show === true && headingClassName ? (
        <h3 className={headingClassName}>Avis vérifiés</h3>
      ) : null}
      {/* Le div `.skeepers_product__reviews` doit être dans le flux DOM normal (pas caché) pour que le script s'initialise */}
      <div
        ref={widgetRootRef}
        className="skeepers_product__reviews"
        data-product-id={ids}
        data-locale={locale}
        data-price={productPrice}
        data-currency={currency}
        data-name={productTitle}
        data-url={productUrl}
        data-image-url={imageUrl}
      />
    </div>
  );
};

export default SkeepersProductReviews;
